import React, { lazy, Suspense, Fragment, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import NavBar from 'containers/navbar';
import 'App.scss';

function isReportDetailRoute(pathname) {
  if (typeof pathname !== 'string') return false;
  return /^\/app\/(iv-report|lev-report|report)\/[^/]+/.test(pathname);
}

const Drawer = lazy(() => import('containers/drawer'));
const BottomBanner = lazy(() => import('components/bottom-banner'));

/* eslint-disable */

const useStyles = makeStyles((theme) => ({
  bannerRoot: {
    // Add a class on icons so global app.scss can exclude with :not(.upf-banner-icon)
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    background: 'rgb(255, 207, 207)',
    color: 'rgb(102, 46, 46)',
    border: '1px solid rgb(255, 180, 180)',
    boxShadow: 'none',
    '& .MuiAlert-icon': { color: 'rgb(102, 46, 46)' },
    '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(102, 46, 46)' },
  },

  warning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    background: 'rgb(252, 244, 199)',
    color: 'rgb(102, 74, 20)',
    border: '1px solid rgb(241, 224, 158)',
    boxShadow: 'none',
    '& .MuiAlert-icon': { color: 'rgb(102, 74, 20)' },
    '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(102, 74, 20)' },
  },

  info: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    background:'rgb(204, 232, 252)',
    color: 'rgb(1, 67, 97)',
    border: '1px solid rgb(171, 211, 240)',
    boxShadow: 'none',
    '& .MuiAlert-icon': { color: 'rgb(1, 67, 97)' },
    '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(1, 67, 97)' },
  },

  success: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    background: 'rgb(217, 252, 222)',
    color: 'rgb(18, 77, 18)',
    border: '1px solid rgb(185, 240, 192)',
    boxShadow: 'none',
    '& .MuiAlert-icon': { color: 'rgb(18, 77, 18)' },
    '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(18, 77, 18)' },
  },
}));

const withBaseElements = WrappedComponent => {
  const BaseElementsComponent = ({ banner }) => {
    const [notification, setNotification] = useState(null);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
      // Load dismissed announcements from localStorage
      try {
        const saved = localStorage.getItem('dismissedUPFAnnouncements');
        return saved ? JSON.parse(saved) : [];
      } catch (error) {
        return [];
      }
    });
    const classes = useStyles();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const scrollFooterWithReport = isReportDetailRoute(location.pathname);

    // Function to determine severity based on announcement type
    const getSeverity = (type) => {
      if (!type) return 'info';
      
      // Map announcement types to alert severities
      switch (type) {
        case 'error':
          return 'error';
        case 'warning':
          return 'warning';
        case 'success':
          return 'success';
        case 'info':
        default:
          return 'info';
      }
    };

    // Strong inline style override for colors (prevents dark mode overrides)
    const getAlertSx = (type) => {
      switch (getSeverity(type)) {
        case 'error':
          return {
            backgroundColor: 'rgb(255, 207, 207) !important',
            color: 'rgb(102, 46, 46) !important',
            border: '1px solid rgb(255, 180, 180) !important',
            boxShadow: 'none !important',
            '& .MuiAlert-icon': { color: 'rgb(102, 46, 46) !important', opacity: '1 !important' },
            '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(102, 46, 46) !important' },
          };
        case 'warning':
          return {
            backgroundColor: 'rgb(252, 244, 199) !important',
            color: 'rgb(102, 74, 20) !important',
            border: '1px solid rgb(241, 224, 158) !important',
            boxShadow: 'none !important',
            '& .MuiAlert-icon': { color: 'rgb(102, 74, 20) !important', opacity: '1 !important' },
            '& .MuiAlert-action .MuiSvgIcon-root, & .MuiTypography-subtitle1': { color: 'rgb(102, 74, 20) !important' },
          };
        case 'success':
          return {
            backgroundColor: 'rgb(217, 252, 222) !important',
            color: 'rgb(18, 77, 18) !important',
            border: '1px solid rgb(185, 240, 192) !important',
            boxShadow: 'none !important',
            '& .MuiAlert-icon': { color: 'rgb(18, 77, 18) !important', opacity: '1 !important' },
            '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(18, 77, 18) !important' },
          };
        case 'info':
        default:
          return {
            backgroundColor: 'rgb(204, 232, 252) !important',
            color: 'rgb(1, 67, 97) !important',
            border: '1px solid rgb(171, 211, 240) !important',
            boxShadow: 'none !important',
            '& .MuiAlert-icon': { color: 'rgb(1, 67, 97) !important', opacity: '1 !important' },
            '& .MuiAlert-action .MuiSvgIcon-root': { color: 'rgb(1, 67, 97) !important' },
          };
      }
    };

    // Provide icon components with a marker class so app.scss can exclude them
    const getIconMapping = () => ({
      success: <CheckCircleIcon className="upf-banner-icon" fontSize="inherit" />,
      warning: <WarningAmberIcon className="upf-banner-icon" fontSize="inherit" />,
      info: <InfoIcon className="upf-banner-icon" fontSize="inherit" />,
      error: <ErrorIcon className="upf-banner-icon" fontSize="inherit" />,
    });


    useEffect(() => {
      const getUPFAnnouncement = async () => {
        let announcement = null
        try {
          let resp = await axios.get(
            `${process.env.REACT_APP_NODE_SERVER_URL}/app/platform-notifications/upf-announcements`
          )

          if (resp.status === 200 && resp.data.announcements && resp.data.announcements.length > 0) {
             const first = resp.data.announcements[0];
             // If this announcement was dismissed this session, don't show until next login
             if (!dismissedAnnouncements.includes(first.uuid)) {
               announcement = first;
             }
          }

        } catch (err) {
          console.error('Error fetching UPF announcements:', err)
          announcement = null
        }

        setNotification(announcement)
      }
      getUPFAnnouncement()
    }, [location.pathname, dismissedAnnouncements])

    const handleDismissAnnouncement = (announcementId) => {
      const newDismissed = [...dismissedAnnouncements, announcementId];
      setDismissedAnnouncements(newDismissed);
      setNotification(null);
      
      // Save to localStorage
      try {
        localStorage.setItem('dismissedUPFAnnouncements', JSON.stringify(newDismissed));
      } catch (error) {
        console.error('Error saving dismissed announcements:', error);
      }
    };

    // Clean up old dismissed announcements when new announcements are fetched
    useEffect(() => {
      if (notification && dismissedAnnouncements.length > 0) {
        // Keep only dismissed announcements that are still relevant
        // This prevents localStorage from growing indefinitely
        const relevantDismissed = dismissedAnnouncements.filter(id => 
          id !== notification.uuid // Keep current announcement's ID if it was dismissed
        );
        
        if (relevantDismissed.length !== dismissedAnnouncements.length) {
          setDismissedAnnouncements(relevantDismissed);
          try {
            localStorage.setItem('dismissedUPFAnnouncements', JSON.stringify(relevantDismissed));
          } catch (error) {
            console.error('Error cleaning up dismissed announcements:', error);
          }
        }
      }
    }, [notification]);

    return (
      <>
        <Suspense fallback={<Fragment />}>
          {notification && <Alert 
            severity={getSeverity(notification.type)} 
            iconMapping={getIconMapping()}
            className={`${classes.bannerRoot} ${classes[getSeverity(notification.type)]}`}
            sx={getAlertSx(notification.type)}
            onClose={() => handleDismissAnnouncement(notification.uuid)}
            action={
              <IconButton
                size="small"
                aria-label="close"
                onClick={() => handleDismissAnnouncement(notification.uuid)}
              >
                <CloseIcon className="upf-banner-icon" fontSize="inherit" />
              </IconButton>
            }
          >
            <Typography component='div' variant='subtitle1'>
              {i18n.language?.includes('fr') ? notification.textFr : notification.textEn}
            </Typography>
          </Alert>}

          <NavBar />

        </Suspense>

        <div className="app-content">
          <Suspense fallback={<Fragment />}>
            <Drawer />
          </Suspense>

          <div className="app-content-body">
            <WrappedComponent />
            {scrollFooterWithReport && (
              <Suspense fallback={<Fragment />}>
                <BottomBanner />
              </Suspense>
            )}
          </div>

          {!scrollFooterWithReport && (
            <Suspense fallback={<Fragment />}>
              <BottomBanner />
            </Suspense>
          )}
        </div>
      </>
    );
  };

  const wrappedComponentName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  BaseElementsComponent.displayName = `Authenticated(${wrappedComponentName})`;

  return BaseElementsComponent;
};

// Note: This file is JavaScript; removed TypeScript type aliases for lint compliance.

export default withBaseElements;
